import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddMembershipSection from "./AddConfigureSections";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import { toast } from "react-toastify";

interface MembershipSection {
  _id: string;
  title: string;
  sortOrder: number;
  isActive: boolean;
  action?: any;
}

const MembershipSections = () => {
  const [showAddSection, setShowAddSection] = useState(false);
  const [sections, setSections] = useState<MembershipSection[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [sectionToEdit, setSectionToEdit] = useState<any | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [loading, setLoading] = useState<boolean>(false);
  const sortFieldMap: Record<string, string> = { createdAtFormatted: "createdAt", sortOrder: "sortOrder", key: "key", title: "title", status: "status" };
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const columns = [
    {
      id: "title",
      label: "Title",
      minWidth: 170,
      // sortable: true,
    },
    // {
    //   id: "key",
    //   label: "Key",
    //   minWidth: 170,
    //   sortable: true,
    // },
    // {
    //   id: "sortOrder",
    //   label: "Sort Order",
    //   minWidth: 170,
    //   sortable: true,
    // },
    {
      id: "isActive",
      label: "Status",
      minWidth: 170,
      // sortable: true,
    },
    {
      id: "action",
      label: "Action",
      minWidth: 100,
      align: "center" as const,
    },
  ];

  useEffect(() => {
    getSections(searchRef.current);
  }, [currentPage, sortField, sortOrder]);

  const getSections = (keyword = "") => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Status filter
    if (search && search !== "ALL") {
      params.append("status", search);
    }

    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    WebService.getAPI({
      action: `memberShipSections/list?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });

        if (!res?.data) return;

        const formattedRows = res.data.map((item: any) => {
          return {
            ...item,
            title: item.title,
            // key: item.key,
            // sortOrder: item.sortOrder,
            isActive: item.isActive ? "Active" : "Inactive",
            action: (
              <div>
                {perm.canEdit && (
                  <PencilFill
                    className="me-3 cursor-pointer"
                    onClick={() => {
                      setShowAddSection(true);
                      setSectionToEdit(item);
                    }}
                    title="Edit"
                  />)}
                {perm.canDelete && (
                  <TrashFill
                    color="#DC143C"
                    className="cursor-pointer"
                    onClick={() => handleDeleteSection(item._id)}
                    title="Delete"
                  />)}
              </div>
            ),
          };
        });

        setSections(formattedRows);
        setTotalCount(res?.pagination?.total || formattedRows.length);
      })
      .catch((err: any) => {
        console.error("Error fetching sections:", err);
      });
  };

  const handleReorder = (newRows: any[]) => {
    setSections(newRows);

    const payload = newRows.map((item: any, index: number) => ({
      _id: item._id,
      sortOrder: index + 1,
    }));

    WebService.postAPI({
      action: "memberShipSections/update-order",
      body: payload,
    })
      .then(() => {
        toast.success("Order updated successfully");
      })
      .catch(() => {
        toast.error("Failed to update order");
      });
  };

  const handleDeleteSection = (sectionId: string) => {
    setSectionToDelete(sectionId);
    setShowDeleteModal(true);
  };

  return (
    <>
      <AddMembershipSection
        show={showAddSection}
        handleClose={() => {
          setShowAddSection(false);
          setSectionToEdit(null);
          getSections(searchRef.current);
        }}
        data={sectionToEdit}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (sectionToDelete) {
            WebService.deleteAPI({
              action: `memberShipSections/delete/${sectionToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              getSections(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete this section?"
      />

      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Configure Section</div>
            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                onClick={() => {
                  setSectionToEdit(null);
                  setShowAddSection(true);
                }}
                style={{ width: 100 }}
              />)}
          </div>
          <div className="row">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title or description"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getSections(searchRef.current);
                }}
              />
            </div>
          </div>
          <CustomTableSortable
            columns={columns}
            rows={sections}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
            draggable                 // ✅ ENABLE DRAG
            onRowsReorder={handleReorder}  // ✅ HANDLE ORDER
          />
        </Card>
      </div>
    </>
  );
};

export default MembershipSections;
